import type { DocAiDocument } from '@/types/api-response/doc-ai-types.ts';
import { defineStore } from 'pinia';
import { reactive, ref } from 'vue';

interface SelectedPdf {
   docId: string;
   docUrl: string;
   bureau: string;
   fileName: string;
   docType: string;
   isUrlDirectlyAccessible: boolean;
   initialPageNo: number;
   type: 'ccr' | 'permit';
}

export const useNmedDocAiUiStore = defineStore('nmed-doc-ai-ui', () => {
   const isLumaAiWindowOpen = ref<boolean>(false);
   const isPdfWindowOpen = ref<boolean>(false);
   const isDocAiWindowOpen = ref<boolean>(false);
   const isMobileDocsSheetOpen = ref<boolean>(false);

   const selectedDoc = reactive<SelectedPdf>({
      docId: '',
      docUrl: '',
      fileName: '',
      bureau: '',
      docType: '',
      isUrlDirectlyAccessible: false,
      initialPageNo: 1,
      type: 'permit',
   });

   const clearSelectedDoc = () => {
      selectedDoc.docId = '';
      selectedDoc.docUrl = '';
      selectedDoc.fileName = '';
      selectedDoc.bureau = '';
      selectedDoc.docType = '';
      selectedDoc.isUrlDirectlyAccessible = false;
      selectedDoc.initialPageNo = 1;
   };

   /**
    * close the PDF window
    */
   const closePdfWindow = () => {
      clearSelectedDoc();
      isPdfWindowOpen.value = false;
   };

   /**
    * close the Document AI window
    */
   const closeDocAiWindow = () => {
      clearSelectedDoc();

      isDocAiWindowOpen.value = false;
   };

   /**
    * Open the mobile documents sheet
    */
   const openMobileDocsSheet = () => {
      isMobileDocsSheetOpen.value = true;
   };

   /**
    * Close the mobile documents sheet
    */
   const closeMobileDocsSheet = () => {
      isMobileDocsSheetOpen.value = false;
   };

   /**
    * Open PDF window with a document
    */
   const openPdfWindow = (doc: DocAiDocument) => {
      selectedDoc.docId = doc.docid;
      selectedDoc.docUrl = doc.docurl;
      selectedDoc.fileName = doc.filename;
      selectedDoc.bureau = doc.Bureau;
      selectedDoc.docType = doc.DocType || '';
      selectedDoc.isUrlDirectlyAccessible = false;
      selectedDoc.initialPageNo = 1;
      selectedDoc.type = 'permit';
      isPdfWindowOpen.value = true;
   };

   /**
    * Open Document AI window with a document
    */
   const openDocAiWindow = (doc: DocAiDocument) => {
      selectedDoc.docId = doc.docid;
      selectedDoc.docUrl = doc.docurl;
      selectedDoc.fileName = doc.filename;
      selectedDoc.bureau = doc.Bureau;
      selectedDoc.docType = doc.DocType || '';
      selectedDoc.isUrlDirectlyAccessible = false;
      selectedDoc.initialPageNo = 1;
      selectedDoc.type = 'permit';
      isDocAiWindowOpen.value = true;
   };

   const $reset = () => {
      isLumaAiWindowOpen.value = false;
      isPdfWindowOpen.value = false;
      isDocAiWindowOpen.value = false;
      isMobileDocsSheetOpen.value = false;
      clearSelectedDoc();
   };

   return {
      isLumaAiWindowOpen,
      isPdfWindowOpen,
      isDocAiWindowOpen,
      isMobileDocsSheetOpen,
      selectedDoc,

      openPdfWindow,
      openDocAiWindow,
      openMobileDocsSheet,
      closePdfWindow,
      closeDocAiWindow,
      closeMobileDocsSheet,

      $reset,
   };
});
